import React from 'react';
import { type CommunityPerspective } from './BiasSpaceMap';
import { useNavigate } from 'react-router-dom';

export interface PerspectiveEntry {
  personaUserId: string;
  personaName: string;
  personaIdeology: string;
  personaColor: string;
  personaSlug: string;
  personaAvatar?: string;
  articleTitle?: string;
  articleSubtitle?: string;
  articleId?: string;
  wordCount?: number;
  lexicalContent?: any;
  reactionCount?: number;
  commentCount?: number;
  steelmanTeaser?: string | null;
  argumentBrief?: {
    thesis: string;
    keyArguments: { label: string; text: string }[];
  } | null;
  pullQuotes?: { text: string; afterParagraph: number }[] | null;
  versusCards?:
    | {
        topic: string;
        thisView: { persona: string; text: string };
        otherView: { persona: string; text: string };
        afterParagraph: number;
      }[]
    | null;
}

interface PersonaTabsProps {
  perspectives: PerspectiveEntry[];
  activePerspectiveId: string;
  onSelect: (personaUserId: string) => void;
  communityPerspectives?: CommunityPerspective[];
  activeCommunityId?: string | null;
  onSelectCommunity?: (articleId: string) => void;
  groupId?: string;
  groupSlug?: string;
  groupTitle?: string;
}

const PersonaTabs: React.FC<PersonaTabsProps> = ({
  perspectives,
  activePerspectiveId,
  onSelect,
  communityPerspectives = [],
  activeCommunityId,
  onSelectCommunity,
  groupId,
  groupSlug,
  groupTitle
}) => {
  const createParams = groupId
    ? `?perspectiveGroupId=${groupId}&perspectiveGroupSlug=${groupSlug || ''}&perspectiveGroupTitle=${encodeURIComponent(groupTitle || '')}`
    : '';

  const navigate = useNavigate();

  const handleAddYours = () => {
    navigate(`/article/create${createParams}`);
  };

  const topCommunity = communityPerspectives.slice(0, 3);

  return (
    <div className="bg-white px-4 md:px-0 pb-0">
      <div className="max-w-[812px] mx-auto">
        {/* AI Perspectives label */}
        <div
          className="text-[10px] font-bold tracking-widest uppercase text-slate-400 mb-1.5 pl-1"
          style={{ fontFamily: "'JetBrains Mono', monospace" }}
        >
          Anchor Perspectives
        </div>

        {/* AI persona tabs */}
        <div className="flex gap-1 rounded-t-xl p-1 pb-0" style={{ background: '#f5f5f4' }}>
          {perspectives.map((p) => {
            const isActive = p.personaUserId === activePerspectiveId;
            const initials = p.personaName
              .split(' ')
              .map((n) => n[0])
              .join('')
              .slice(0, 2);
            return (
              <button
                key={p.personaUserId}
                onClick={() => onSelect(p.personaUserId)}
                className="flex-1 flex items-center justify-center gap-2.5 border-none cursor-pointer transition-all duration-200"
                style={{
                  padding: '12px',
                  borderRadius: '10px 10px 0 0',
                  background: isActive ? 'white' : 'transparent',
                  boxShadow: isActive ? '0 -2px 8px rgba(0,0,0,0.04)' : 'none'
                }}
              >
                <div className="relative shrink-0">
                  {p.personaAvatar ? (
                    <img
                      src={p.personaAvatar}
                      alt={p.personaName}
                      className="rounded-full object-cover transition-all duration-200"
                      style={{
                        width: '32px',
                        height: '32px',
                        opacity: isActive ? 1 : 0.5
                      }}
                    />
                  ) : (
                    <div
                      className="flex items-center justify-center rounded-full text-white font-bold transition-all duration-200"
                      style={{
                        width: '32px',
                        height: '32px',
                        background: isActive ? p.personaColor : '#d6d3d1',
                        fontSize: '11px',
                        fontFamily: "'JetBrains Mono', monospace"
                      }}
                    >
                      {initials}
                    </div>
                  )}
                  <div
                    className="absolute font-extrabold rounded-sm border-[1.5px] border-white"
                    style={{
                      bottom: '-2px',
                      right: '-4px',
                      background: '#0f172a',
                      color: '#a5f3fc',
                      fontSize: '6px',
                      padding: '1px 3px',
                      fontFamily: "'JetBrains Mono', monospace"
                    }}
                  >
                    AI
                  </div>
                </div>
                <div className="text-left hidden sm:block">
                  <div
                    className="text-[13px] font-bold transition-all duration-200"
                    style={{ color: isActive ? '#0f172a' : '#a8a29e' }}
                  >
                    {p.personaName}
                  </div>
                  <div
                    className="text-[10px] font-semibold transition-all duration-200"
                    style={{
                      color: isActive ? p.personaColor : '#d6d3d1',
                      fontFamily: "'JetBrains Mono', monospace"
                    }}
                  >
                    {p.personaIdeology}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Community Perspectives section */}
        <div className="mt-3">
          {/* Label row */}
          <div className="flex items-center mb-1.5 pl-1">
            <div
              className="text-[10px] font-bold tracking-widest uppercase text-slate-400 flex-1"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              Featured Community Perspectives
            </div>
            <button
              onClick={handleAddYours}
              className="flex items-center gap-1.5 px-3 py-1.5 border-none cursor-pointer rounded-lg transition hover:bg-stone-100"
              style={{ background: 'transparent' }}
            >
              <div className="w-6 h-6 rounded-full border-2 border-dashed border-stone-300 flex items-center justify-center text-stone-300 text-sm leading-none">
                +
              </div>
              <span
                className="text-[10px] font-semibold text-stone-400"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                Add yours
              </span>
            </button>
          </div>

          {/* Community tabs or empty state */}
          {topCommunity.length === 0 ? (
            <div
              className="rounded-t-xl px-4 py-3 text-[11px] text-slate-400"
              style={{ background: '#f5f5f4', fontFamily: "'JetBrains Mono', monospace" }}
            >
              No community responses yet — be the first.
            </div>
          ) : (
            <div className="flex gap-1 rounded-t-xl p-1 pb-0" style={{ background: '#f5f5f4' }}>
              {topCommunity.map((cp) => {
                const isActive = cp.articleId === activeCommunityId;
                const initials = (cp.authorName || '??')
                  .split(' ')
                  .map((n: string) => n[0])
                  .join('')
                  .slice(0, 2)
                  .toUpperCase();
                return (
                  <button
                    key={cp.articleId}
                    onClick={() => onSelectCommunity?.(cp.articleId)}
                    className="flex-1 flex items-center justify-center gap-2.5 border-none cursor-pointer transition-all duration-200"
                    style={{
                      padding: '12px',
                      borderRadius: '10px 10px 0 0',
                      background: isActive ? 'white' : 'transparent',
                      boxShadow: isActive ? '0 -2px 8px rgba(0,0,0,0.04)' : 'none'
                    }}
                  >
                    <div className="relative shrink-0">
                      {cp.authorAvatar ? (
                        <img
                          src={cp.authorAvatar}
                          alt={cp.authorName}
                          className="rounded-full object-cover transition-all duration-200"
                          style={{
                            width: '32px',
                            height: '32px',
                            opacity: isActive ? 1 : 0.5
                          }}
                        />
                      ) : (
                        <div
                          className="flex items-center justify-center rounded-full text-white font-bold transition-all duration-200"
                          style={{
                            width: '32px',
                            height: '32px',
                            background: isActive ? '#059669' : '#d6d3d1',
                            fontSize: '11px',
                            fontFamily: "'JetBrains Mono', monospace"
                          }}
                        >
                          {initials}
                        </div>
                      )}
                      <div
                        className="absolute font-extrabold rounded-sm border-[1.5px] border-white"
                        style={{
                          bottom: '-2px',
                          right: '-4px',
                          background: '#059669',
                          color: '#ffffff',
                          fontSize: '6px',
                          padding: '1px 3px',
                          fontFamily: "'JetBrains Mono', monospace"
                        }}
                      >
                        ✦
                      </div>
                    </div>
                    <div className="text-left hidden sm:block">
                      <div
                        className="text-[13px] font-bold transition-all duration-200 truncate max-w-[120px]"
                        style={{ color: isActive ? '#0f172a' : '#a8a29e' }}
                      >
                        {cp.authorName}
                      </div>
                      <div
                        className="text-[10px] font-semibold transition-all duration-200"
                        style={{
                          color: isActive ? '#059669' : '#d6d3d1',
                          fontFamily: "'JetBrains Mono', monospace"
                        }}
                      >
                        {cp.twygCount ?? 0} twygs
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PersonaTabs;
